/**
 * A lista, virtualizada.
 *
 * O painel anterior punha os 478 cartões no DOM de uma vez. Funcionava com
 * 478; com alguns milhares o navegador engasga na rolagem. Aqui só o que
 * está na tela existe — e a lista é uma lista de verdade, com <ul>/<li>,
 * porque a versão anterior usava <button> soltos com aria-selected, que é
 * ARIA inválida em botão e não anuncia nada.
 *
 * ── O CARTÃO SEGUE O DESENHO DA GESTÃO (19/10) ────────────────────────────
 *
 * Em cima, à esquerda: o tipo de serviço, a situação da oportunidade (a aba em
 * que ela está), o órgão em destaque, o local com o código e o resumo do
 * objeto numa linha. À direita, separado por um filete, o score. Embaixo, depois
 * de uma linha: o valor, o encerramento e o "Ver detalhes". É só isso — o
 * que a IA leu, o veredito dela e quem decidiu continuam inteiros no detalhe,
 * a um clique.
 *
 * Os dois fatos da regra (o valor abaixo do piso e o prazo vencido, "as únicas
 * labels que devem existir", o Pedro, 14/10) entram nas células do rodapé: o
 * rótulo do valor DIZ que está abaixo do piso, e o prazo vencido se lê no
 * encerramento — sem uma linha a mais, que a altura fixa não comporta.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer, useWindowVirtualizer } from "@tanstack/react-virtual";
import type { LicitacaoResumo } from "../api/cliente";
import { Nota } from "./Selos";
import { diaMes, fraseLegivel, nomeLegivel, prazoEm, situacaoDe, tiposDeServico, valorCurto } from "../formato";
import css from "./ListaLicitacoes.module.css";
import { useMidia } from "../useMidia";

/** "29/10 · faltam 10 dias", "amanhã", "encerrada" — o encerramento do rodapé,
 *  em três pedaços: o "faltam" é o que sai primeiro quando o cartão é estreito. */
function encerramentoDe(iso: string | null | undefined) {
  const p = prazoEm(iso);
  // "Sem prazo", e não "Sem prazo informado": o rótulo da célula já diz
  // "Encerramento", e a frase longa não cabia ao lado do valor no celular.
  if (!iso) return { data: "Sem prazo", prefixo: "", resto: "", nivel: p.nivel };
  const dias = p.texto.startsWith("em ");
  return {
    data: `${diaMes(iso)} · `,
    prefixo: dias ? "faltam " : "",
    resto: dias ? p.texto.slice(3) : p.texto,
    nivel: p.nivel,
  };
}

interface Props {
  itens: LicitacaoResumo[];
  carregando: boolean;
  selecionada: string | null;
  aoSelecionar: (id: string) => void;
  /** Aprovadas e Reprovadas: os itens já vêm pela data da decisão, e a lista
   * põe o nome do dia antes do primeiro de cada dia. */
  porDia?: boolean;
}

/** Uma linha da lista virtual: o separador de um dia, ou um cartão. */
type Linha =
  | { tipo: "dia"; chave: string; rotulo: string; quantas: number }
  | { tipo: "item"; i: LicitacaoResumo };

/** A ALTURA DO SEPARADOR DO DIA: a estimativa até a primeira medida. */
const ESTIMATIVA_DIA = 46;

const FUSO = "America/Recife";
const chaveDoDia = (iso: string) =>
  new Date(iso).toLocaleDateString("en-CA", { timeZone: FUSO }); // AAAA-MM-DD

/** "Hoje", "Ontem", ou "segunda-feira, 19 de outubro" (com o ano, se não for
 * o deste ano) — no dia de Recife, e não no do navegador. */
function nomeDoDia(iso: string): string {
  const chave = chaveDoDia(iso);
  const hoje = chaveDoDia(new Date().toISOString());
  const ontem = chaveDoDia(new Date(Date.now() - 86_400_000).toISOString());
  if (chave === hoje) return "Hoje";
  if (chave === ontem) return "Ontem";
  const d = new Date(iso);
  const mesmoAno = chave.slice(0, 4) === hoje.slice(0, 4);
  const texto = d.toLocaleDateString("pt-BR", {
    timeZone: FUSO,
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(mesmoAno ? {} : { year: "numeric" }),
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/** Os cartões com o separador de cada dia antes do primeiro dele. Quem não
 * tem data de decisão fica num grupo próprio, no fim. */
function comDias(itens: LicitacaoResumo[]): Linha[] {
  const linhas: Linha[] = [];
  let atual: { tipo: "dia"; chave: string; rotulo: string; quantas: number } | null = null;
  for (const i of itens) {
    const chave = i.decidido_em ? chaveDoDia(i.decidido_em) : "sem-data";
    if (!atual || atual.chave !== chave) {
      atual = {
        tipo: "dia",
        chave,
        rotulo: i.decidido_em ? nomeDoDia(i.decidido_em) : "Sem data de decisão",
        quantas: 0,
      };
      linhas.push(atual);
    }
    atual.quantas += 1;
    linhas.push({ tipo: "item", i });
  }
  return linhas;
}

/**
 * A ALTURA DO CARTAO E FIXA E IGUAL EM TODOS, E OS TAMANHOS DE LETRA TAMBEM (o
 * Pedro, 19/10: "os cards nao podem divergir em altura", "todos os cards devem
 * ser sempre padroes").
 *
 * O cartao reserva duas linhas para o orgao e uma para o tipo, o local e o
 * resumo; o orgao que passa de duas linhas e cortado (o nome inteiro fica no
 * `title`) — uma tentativa de encolher a letra do nome grande fazia um cartao
 * parecer outro. O rodape fica sempre numa linha so: o botao vira so a seta e
 * o "faltam" cai quando o cartao e estreito.
 *
 * A CONTA esta no CSS (`.cartao`): 235, mais os 10 do vao entre cartoes — e 279
 * no celular, onde o cartao muda de arranjo (a mesma para todos, em cada largura). O
 * virtualizador continua MEDINDO o `<li>` (`measureElement`), que e o proprio
 * cartao — posicao e realidade saem do mesmo elemento, e uma conta errada
 * aqui nao sobrepoe cartao nenhum; a estimativa so vale ate a primeira
 * medida.
 */
const ESTIMATIVA = 245;

/** A media query casa agora? E re-renderiza quando deixa de casar. */
export function ListaLicitacoes({ itens, carregando, selecionada, aoSelecionar, porDia }: Props) {
  const caixa = useRef<HTMLDivElement>(null);
  const lista = useRef<HTMLUListElement>(null);
  // Abaixo de 980px a página é a rolagem, e não a caixa: caixa com rolagem
  // própria dentro de uma página que também rola prendia o dedo, e no iOS o
  // 100vh da `max-height` é o da tela sem a barra do Safari.
  const estreita = useMidia("(max-width: 980px)");
  const [margem, setMargem] = useState(0);

  const linhas = useMemo<Linha[]>(
    () => (porDia ? comDias(itens) : itens.map((i) => ({ tipo: "item" as const, i }))),
    [itens, porDia],
  );
  const estima = (n: number) => (linhas[n]?.tipo === "dia" ? ESTIMATIVA_DIA : ESTIMATIVA);

  const naCaixa = useVirtualizer({
    count: linhas.length,
    getScrollElement: () => caixa.current,
    estimateSize: estima,
    overscan: 8,
    enabled: !estreita,
  });
  const naJanela = useWindowVirtualizer({
    count: linhas.length,
    estimateSize: estima,
    overscan: 8,
    scrollMargin: margem,
    enabled: estreita,
  });
  const virtual = estreita ? naJanela : naCaixa;

  // ONDE A LISTA COMEÇA NA PÁGINA, para o virtualizador da janela. Não por
  // ResizeObserver no `body`: ele tem `height: 100%` (tokens.css), e o tamanho
  // dele não muda quando o que está acima da lista cresce.
  //
  // Medida a cada render — troca de aba, aviso novo, rolagem (o virtualizador
  // re-renderiza quando ela começa) — e quando algo acima muda de tamanho sem
  // render nenhum aqui, como os filtros abrindo. Margem velha desenharia o
  // trecho errado da lista.
  const margemAtual = useRef(0);
  const mede = () => {
    const ul = lista.current;
    if (!ul) return;
    const topo = ul.getBoundingClientRect().top + window.scrollY;
    if (Math.abs(topo - margemAtual.current) >= 1) {
      margemAtual.current = topo;
      setMargem(topo);
    }
  };
  useLayoutEffect(() => {
    if (estreita) mede();
  });
  const temLista = itens.length > 0;
  useEffect(() => {
    const ul = lista.current;
    if (!estreita || !ul) return;
    // Tudo o que vem antes da lista, subindo até o body: os filtros, as abas,
    // o topo da tela, a barra da moldura.
    const ro = new ResizeObserver(() => mede());
    for (let el: Element | null = ul; el && el !== document.body; el = el.parentElement) {
      for (let antes = el.previousElementSibling; antes; antes = antes.previousElementSibling) {
        ro.observe(antes);
      }
    }
    return () => ro.disconnect();
  }, [estreita, temLista]);

  if (carregando && !itens.length) {
    return <div className={css.vazio}>Lendo o acervo…</div>;
  }
  if (!itens.length) {
    return (
      <div className={css.vazio}>
        <b>Nada casa com esses filtros.</b>
        <p>
          Tente limpar um deles, ou faça uma varredura nova a partir da tela
          inicial — o acervo só tem o que já foi buscado.
        </p>
      </div>
    );
  }

  return (
    <div ref={caixa} className={css.caixa}>
      <ul
        ref={lista}
        className={css.lista}
        style={{ height: virtual.getTotalSize() }}
        aria-label={`${itens.length} licitações`}
      >
        {virtual.getVirtualItems().map((v) => {
          const linha = linhas[v.index]!;
          if (linha.tipo === "dia") {
            return (
              <li
                key={`dia-${linha.chave}`}
                data-index={v.index}
                ref={virtual.measureElement}
                className={`${css.linha} ${css.dia}`}
                style={{ transform: `translateY(${v.start - virtual.options.scrollMargin}px)` }}
              >
                <span className={css.diaNome}>{linha.rotulo}</span>
                <span className={css.diaConta}>
                  {linha.quantas} {linha.quantas === 1 ? "decisão" : "decisões"}
                </span>
              </li>
            );
          }
          const i = linha.i;
          const ativa = i.id === selecionada;
          const tipo = tiposDeServico(i);
          const situacao = situacaoDe(i);
          const prazo = encerramentoDe(i.encerramento);
          const valor = valorCurto(i.valor_estimado);
          // Os dois fatos da regra, nas células do rodapé (ver o alto do arquivo).
          const abaixoDoPiso = i.etiquetas?.find((e) => e.tipo === "abaixo_do_piso");
          const prazoVencido = i.etiquetas?.some((e) => e.tipo === "prazo_vencido");
          // O texto da fonte legível (28/10), como no detalhe.
          const municipio = nomeLegivel(i.municipio);
          const orgao = nomeLegivel(i.orgao, [municipio]);
          const objeto = fraseLegivel(i.objeto, [municipio]);
          const local = [municipio, i.uf].filter(Boolean).join(" / ");
          return (
            <li
              key={i.id}
              data-index={v.index}
              ref={virtual.measureElement}
              className={css.linha}
              // SEM `height`: o <li> e o conteudo, e `measureElement` o mede.
              // `start` conta do topo da pagina na janela (`scrollMargin`), e o
              // <li> e posicionado dentro do <ul>.
              style={{
                transform: `translateY(${v.start - virtual.options.scrollMargin}px)`,
              }}
            >
              <button
                type="button"
                className={[
                  css.cartao,
                  ativa ? css.ativa : "",
                  // DESCARTADA POR UMA PESSOA, marcada por uma barra lateral.
                  //
                  // `decisao`, e NAO `recomendacao`. A barra nasceu ligada a
                  // recomendacao, que e a maquina opinando, e o resultado era
                  // uma licitacao na aba "Avaliar" — onde ninguem tocou nela
                  // ainda — pintada como recusada. Cor forte e decisao de
                  // PESSOA: verde para aprovada, esta barra para reprovada.
                  // A leitura da maquina continua no rodape, por extenso.
                  i.decisao === "descartada" ? css.recusada : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-current={ativa ? "true" : undefined}
                onClick={() => aoSelecionar(i.id)}
              >
                <span className={css.principal}>
                  <span className={css.topo}>
                    <span
                      className={`${css.tipo} ${tipo.classificado ? "" : css.semTipo}`}
                      title={tipo.classificado ? "Tipo de serviço da oportunidade" : undefined}
                    >
                      {tipo.nomes.join(" · ")}
                    </span>
                    {situacao ? (
                      <span className={`${css.situacao} ${css[`tom_${situacao.tom}`]}`}>
                        {situacao.texto}
                      </span>
                    ) : null}
                  </span>
                  <span className={css.orgao} title={orgao || undefined}>
                    <span className={css.orgaoTexto}>{orgao || "Órgão não informado"}</span>
                  </span>
                  <span className={css.local}>
                    {local || "Local não informado"}
                    {/* O CÓDIGO: é como uma pessoa cita esta oportunidade
                        numa conversa — "olha a 2026-09-2171". */}
                    {i.codigo ? <span className={css.edital}>Edital {i.codigo}</span> : null}
                  </span>
                  <span className={css.resumo} title={objeto}>
                    <b>Resumo:</b> {objeto}
                  </span>
                </span>

                <span className={css.placar}>
                  <Nota
                    valor={i.score}
                    faixa={i.faixa}
                    decisao={i.decisao}
                    recomendacao={i.recomendacao}
                  />
                </span>

                <span className={css.rodape}>
                  <span className={css.dado}>
                    <span className={`${css.rotulo} ${abaixoDoPiso ? css.rotuloAviso : ""}`}
                          title={abaixoDoPiso?.texto}>
                      {abaixoDoPiso ? "Abaixo do piso" : "Valor estimado"}
                    </span>
                    {/* DE ONDE VEIO O VALOR (0149): o sigilo declarado pelo órgão é
                        um fato, e não "não informado"; a prova vai no `title`. */}
                    <b className={valor ? "" : css.semValor}
                       title={i.valor_prova ?? i.valor_estimado ?? undefined}>
                      {valor ?? (i.valor_origem === "sigiloso" ? "Sigiloso" : "Não informado")}
                    </b>
                  </span>
                  <span className={css.dado}>
                    <span className={`${css.rotulo} ${prazoVencido ? css.rotuloVencido : ""}`}>
                      {prazoVencido ? "Prazo vencido" : "Encerramento"}
                    </span>
                    <b className={css[`prazo_${prazo.nivel}`]}>
                      {prazo.data}
                      {prazo.prefixo ? <span className={css.faltam}>{prazo.prefixo}</span> : null}
                      {prazo.resto}
                    </b>
                  </span>
                  <span className={css.ver}>
                    <span className={css.verTexto}>Ver detalhes</span>
                    <svg className={css.verSeta} viewBox="0 0 16 16" width="16" height="16" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                         strokeLinejoin="round" aria-hidden="true">
                      <path d="M3 8h10M9 4l4 4-4 4" />
                    </svg>
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
